import { configureStore, createListenerMiddleware, type ThunkDispatch, type UnknownAction } from '@reduxjs/toolkit';
import { enableMapSet } from 'immer';
import { type TypedUseSelectorHook, useDispatch, useSelector, useStore } from 'react-redux';
import type { Observable } from 'rxjs';
import { intlDefaultFormatter, type IntlWithFormatter } from '../../contexts/IntlContext';
import type { CustomerBalanceTransaction, CustomerOrder } from '../../types';
import type { IWebSocketClient } from '../WebSocketClient';
import type { InitialReferenceDataParams } from './state';
import { referenceDataSliceReducer } from './state/OMSReferenceDataSlice/reducer';
import { orderSliceReducer } from './state/OrderSlice/reducer';
import { getOrderInitialState } from './state/OrderSlice/utils';
import { sessionSliceReducer } from './state/SessionSlice/reducer';
import { getSwapInitialState } from './state/SwapSlice/initialState';
import { swapSliceReducer } from './state/SwapSlice/reducer';
import { setupSwapSliceListeners } from './state/SwapSlice/thunks/sendQuoteRequestMiddleWare';

enableMapSet();

export const getWLOrderFormStore = (
  params?: InitialReferenceDataParams,
  extra?: WLAppExtraArgument,
  intl: IntlWithFormatter = intlDefaultFormatter
) => {
  const listenerMiddleware = createListenerMiddleware({
    extra,
  });
  const startListening = buildWLAppStateListener(listenerMiddleware);
  setupSwapSliceListeners(startListening);

  return configureStore({
    preloadedState: {
      order: getOrderInitialState(params, intl),
      referenceData: {
        securities: params ? params.securities : { securitiesBySymbol: new Map(), securitiesList: [] },
        marketAccounts: params
          ? params.marketAccounts
          : { customerMarketAccountsBySourceAccountID: new Map(), customerMarketAccountsList: [] },
        strategiesBySymbol: params?.strategiesBySymbol || new Map(),
        currencies: params ? params.currencies : { currenciesBySymbol: new Map(), currenciesList: [] },
        favoredPairCurrency: params?.favoredPairCurrency || undefined,
        homeCurrency: params?.homeCurrency || undefined,
      },
      swap: getSwapInitialState(params, intl),
    },
    reducer: {
      order: orderSliceReducer,
      referenceData: referenceDataSliceReducer,
      session: sessionSliceReducer,
      swap: swapSliceReducer,
    },
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        serializableCheck: false,
        immutableCheck: false,
        thunk: {
          extraArgument: extra,
        },
      }).concat(listenerMiddleware.middleware),
    devTools: true,
  });
};

export type WLAppStore = ReturnType<typeof getWLOrderFormStore>;
export type WLRootState = ReturnType<WLAppStore['getState']>;
export type WLAppDispatch = WLAppStore['dispatch'];

export const useWLOrderFormStore = () => useStore<WLAppStore>();
export const useWLOrderFormDispatch = () => useDispatch<WLAppDispatch>();
export const useWLOrderFormSelector: TypedUseSelectorHook<WLRootState> = useSelector;

export interface WLAppExtraArgument {
  wsClient: IWebSocketClient<unknown>;
  sessionOrdersObs: Observable<CustomerOrder>;
  sessionTransactionsObs: Observable<CustomerBalanceTransaction>;
}

export type WLAppStateListenerStart = ReturnType<typeof buildWLAppStateListener>;
export const buildWLAppStateListener = (listenerMiddleware: ReturnType<typeof createListenerMiddleware>) => {
  return listenerMiddleware.startListening.withTypes<
    WLRootState,
    ThunkDispatch<WLRootState, WLAppExtraArgument, UnknownAction>,
    WLAppExtraArgument
  >();
};
